import type { HelpSectionsMap } from './types';

export const frHelpSections: HelpSectionsMap = {
  start: {
    title: 'Premiers pas',
    intro: 'Guide rapide pour démarrer dans WERSTACK. Suivez les étapes dans l\'ordre pour vous familiariser plus rapidement.',
    blocks: [
      {
        title: 'Étape 1. Choisir un workspace',
        text: 'Un workspace est votre projet avec ses propres fichiers, paramètres et membres d\'équipe.',
        items: [
          'Dans la barre latérale gauche, ouvrez « Mes » ou « Partagés » et sélectionnez un workspace.',
          'Les fichiers et dossiers se mettent à jour automatiquement.',
          '« Créer » en haut de la barre latérale — nouveau workspace personnel (si la limite n\'est pas atteinte).',
          'Chaque workspace peut avoir sa propre langue, son thème et sa couleur d\'accent.',
        ],
        where: 'Barre latérale gauche → « Mes » / « Partagés ». Lorsqu\'elle est repliée, survolez l\'icône d\'une section pour afficher la liste.',
      },
      {
        title: 'Étape 2. Importer vos premiers fichiers',
        items: [
          'Cliquez sur « Importer des images » dans la barre d\'outils de la galerie.',
          'Ctrl+Shift+V — coller une capture d\'écran depuis le presse-papiers dans le dossier actif.',
          'Jusqu\'à 1000 fichiers à la fois (JPEG, PNG, WebP ; 10 Mo maximum chacun).',
          'Une barre de progression apparaît en bas — vous pouvez la fermer ou l\'annuler.',
        ],
        where: 'Écran d\'accueil → bouton « Importer des images » en haut à droite.',
      },
      {
        title: 'Étape 3. Créer des dossiers',
        items: [
          'Cliquez sur « Créer un dossier », saisissez un nom et confirmez.',
          'Ouvrez un dossier par double-clic (simple appui sur écran tactile).',
          'À la racine du workspace, la barre d\'outils affiche le nom du workspace à gauche.',
          'Dans un sous-dossier — fil d\'Ariane avec icône d\'accueil pour revenir à la racine.',
        ],
        where: 'Écran d\'accueil → « Créer un dossier » à côté de l\'import.',
      },
      {
        title: 'Barre latérale',
        items: [
          'Logo WERSTACK et listes de workspaces en haut.',
          '« Créer » — nouveau workspace.',
          '« Données » — tableaux : « Tous » ouvre le catalogue ; listes en dessous pour un accès rapide.',
          '« Tableaux » — kanban sur les tables.',
          '« Workflows » — graphes d\'automation de l\'espace actuel.',
          '« Galeries » — catalogue des galeries publiques.',
          'Bouton sur le bord — replier ou déplier la barre latérale.',
        ],
        where: 'Colonne gauche de l\'écran principal.',
      },
      {
        title: 'Boutons de la barre supérieure',
        items: [
          'Cloche — invitations d\'équipe et notifications de commentaires.',
          'Point d\'interrogation — ce panneau d\'aide (séparé du profil).',
          'Initiales — déconnexion.',
        ],
        where: 'Coin supérieur droit de l\'écran.',
      },
      {
        title: 'Rôles et permissions',
        text: 'Toutes les actions ne sont pas disponibles pour tous les utilisateurs.',
        items: [
          'Propriétaire et Admin — accès complet, gestion des membres et des galeries.',
          'Éditeur — consultation et modification des fichiers, sans gestion d\'équipe.',
          'Lecteur — consultation uniquement, sans import ni suppression.',
        ],
        tip: 'Si un bouton est désactivé ou qu\'un élément de menu est absent, vous avez peut-être le rôle Lecteur.',
      },
    ],
  },
  gallery: {
    title: 'Galerie : fichiers et dossiers',
    intro: 'Écran principal où vos images sont stockées et organisées.',
    blocks: [
      {
        title: 'Barre d\'outils',
        items: [
          'À gauche — nom du workspace (à la racine), fil d\'Ariane (dans un sous-dossier) ou indicateur de recherche/filtre actif.',
          'À droite — import, création de dossier, recherche, filtre par tags, tri, vue grille/liste, corbeille.',
        ],
        where: 'Barre sous l\'en-tête supérieur de l\'application.',
      },
      {
        title: 'Modes d\'affichage',
        items: [
          'Grille — cartes avec aperçu, likes et indicateur de commentaires.',
          'Liste — tableau : nom, date, type, taille ; pratique pour la sélection multiple.',
        ],
        where: 'Bouton de bascule (icônes grille et liste) à droite de la barre d\'outils.',
      },
      {
        title: 'Recherche de fichiers',
        items: [
          'Cliquez sur l\'icône loupe et saisissez au moins 2 caractères.',
          'La recherche porte sur le nom du fichier, les tags et le texte des commentaires.',
          'Pour quitter la recherche — cliquez sur « Effacer » ou videz le champ.',
        ],
        where: 'Icône loupe dans la barre d\'outils. Tri et import sont masqués pendant la recherche.',
      },
      {
        title: 'Filtre par tags',
        items: [
          'Cliquez sur l\'icône tag et sélectionnez un ou plusieurs tags.',
          'Les tags sélectionnés apparaissent sous forme de puces à gauche — retirez-les avec ×.',
          'Avec des tags actifs, « Créer une galerie par tags » apparaît (voir Galeries publiques).',
        ],
        where: 'Icône tag dans la barre d\'outils.',
      },
      {
        title: 'Tri',
        items: [
          'Par date d\'import (plus récent ou plus ancien en premier).',
          'Par nom (A–Z ou Z–A).',
          'Par taille de fichier.',
          'Par nombre de likes.',
        ],
        where: 'Icône curseurs dans la barre d\'outils. Indisponible dans la corbeille, la recherche ou le filtre par tags.',
      },
      {
        title: 'Likes',
        items: [
          'Cliquez sur le cœur d\'une carte pour liker ou retirer le like.',
          'Le like n\'ouvre pas le fichier — cliquez sur l\'aperçu ou le nom pour l\'ouvrir.',
        ],
      },
      {
        title: 'Pagination',
        text: 'Lorsqu\'il y a beaucoup de fichiers, utilisez le panneau en bas pour changer de page et le nombre d\'éléments par page.',
        where: 'Barre de pagination en bas de l\'écran.',
      },
    ],
  },
  selection: {
    title: 'Sélection et actions',
    intro: 'Comment sélectionner plusieurs fichiers et dossiers et quelles actions effectuer.',
    blocks: [
      {
        title: 'Sélection à la souris',
        items: [
          'Cliquez sur un élément — l\'ajouter ou le retirer de la sélection.',
          'Shift + clic — sélectionner une plage à partir du dernier élément sélectionné.',
          'Glissez un cadre sur une zone vide — sélectionner tout ce qui est à l\'intérieur.',
          'Ctrl (Cmd sur Mac) + cadre — ajouter à la sélection actuelle.',
          'Ctrl+A — tout sélectionner sur la page.',
          'Escape ou clic à l\'extérieur — effacer la sélection.',
        ],
      },
      {
        title: 'Sélection tactile',
        items: [
          'Appui simple — ouvrir un fichier ou un dossier.',
          'Double appui sur une carte — ajouter à la sélection.',
          'Glisser sur une zone vide (rubber band) — sélection multiple.',
          'Appui long sur une zone vide — menu contextuel.',
        ],
      },
      {
        title: 'Glisser-déposer',
        items: [
          'Glissez un fichier ou un dossier sur un autre dossier pour le déplacer.',
          'Si l\'élément est déjà sélectionné, toute la sélection est déplacée.',
          'Sur mobile, le glisser tactile affiche une carte flottante en aperçu.',
        ],
        tip: 'Le glisser-déposer est désactivé dans la corbeille.',
      },
      {
        title: 'Couper et coller',
        items: [
          'Sélectionnez des éléments → « Couper » dans le menu contextuel ou Ctrl+X.',
          'Allez dans le dossier cible → « Coller » ou Ctrl+V.',
          'Impossible de coller dans le même dossier d\'où provient la coupure.',
        ],
      },
      {
        title: 'Menu contextuel',
        text: 'Clic droit dans la galerie (ou appui long sur écran tactile) pour les actions.',
        items: [
          'Importer des images / Créer un dossier.',
          'Couper, Coller.',
          'Renommer le dossier (exactement un dossier sélectionné).',
          'Créer une galerie (exactement un dossier sélectionné).',
          'Ajouter un tag (fichiers de la sélection uniquement).',
          'Générer — images sélectionnées (éditeurs et admins).',
          'Supprimer (envoi vers la corbeille).',
          'Restaurer (corbeille uniquement).',
        ],
        tip: 'Les raccourcis clavier sont affichés à côté des éléments du menu.',
      },
      {
        title: 'Opérations groupées',
        items: [
          'Supprimer la sélection — vers la corbeille (avec confirmation).',
          'Ajouter un tag — à tous les fichiers sélectionnés en une fois.',
          'Couper et coller — déplacer un groupe de fichiers.',
        ],
      },
      {
        title: 'Génération d\'image par IA (galerie)',
        items: [
          'Sélectionnez une ou plusieurs images → menu contextuel → Générer.',
          'Une nouvelle image par fichier sélectionné, les unes après les autres. Téléchargez ou enregistrez le résultat — les originaux ne changent pas.',
        ],
      },
    ],
  },
  viewer: {
    title: 'Visionneuse de fichiers',
    intro: 'Fenêtre plein écran avec informations du fichier, commentaires et actions.',
    blocks: [
      {
        title: 'Comment ouvrir',
        items: [
          'Cliquez sur une carte de fichier dans la galerie (ou sur le nom en vue liste).',
          'En mode sélection multiple — double-clic / double appui.',
        ],
      },
      {
        title: 'Navigation entre les fichiers',
        items: [
          'Flèches Précédent / Suivant sur les côtés de la fenêtre.',
          'Touches ← et → du clavier.',
          'Escape — fermer (ou quitter la vue plein écran de l\'image).',
        ],
      },
      {
        title: 'Onglet Infos',
        items: [
          'Nom, résolution, taille, date d\'import.',
          'Tags — cliquez sur un tag pour filtrer la galerie.',
          'Boutons : Modifier, Télécharger, Créer/révoquer le lien public, Copier l\'URL.',
        ],
        where: 'Panneau droit de la modale → onglet Infos.',
      },
      {
        title: 'Tags',
        items: [
          'Icône tag dans le coin — ajouter un nouveau tag.',
          '× sur une puce tag — retirer le tag.',
        ],
      },
      {
        title: 'Génération d\'image par IA',
        items: [
          'Icône d\'étincelles à côté de Tag — décrivez le fond, puis générez une nouvelle image produit à partir du fichier actuel.',
          'L\'aperçu reste dans la modale : téléchargez ou enregistrez comme nouveau fichier dans un dossier (l\'original n\'est pas écrasé).',
        ],
        where: 'Barre de la visionneuse → icône d\'étincelles (éditeurs et admins).',
      },
      {
        title: 'Lien public pour un fichier',
        items: [
          '« Créer un lien public » — toute personne disposant du lien peut consulter sans connexion.',
          '« Révoquer le lien » — l\'accès est interrompu.',
          '« Copier l\'URL » — copier le lien dans le presse-papiers.',
        ],
        tip: 'Il s\'agit d\'un lien pour un fichier unique. Pour les albums, utilisez Galeries publiques.',
      },
      {
        title: 'Onglet Commentaires',
        items: [
          'Rédigez des commentaires et des réponses sur le fichier.',
          'Modifiez et supprimez vos propres commentaires.',
          'Ajoutez des réactions emoji aux commentaires.',
          'Mentionnez des collègues avec @ — commencez à saisir le nom ou l\'e-mail.',
        ],
        where: 'Panneau droit → onglet Commentaires. Les alertes de nouveaux commentaires apparaissent dans la cloche.',
      },
      {
        title: 'Vue plein écran',
        text: 'Cliquez sur l\'icône d\'agrandissement de l\'image pour l\'afficher en plein écran.',
      },
    ],
  },
  editor: {
    title: 'Éditeur d\'images',
    intro: 'Ouvrez un fichier depuis la visionneuse avec « Modifier ». Le canvas est composé de calques : frame, images, texte et formes. Toutes les modifications peuvent être annulées.',
    blocks: [
      {
        title: 'Barre supérieure',
        items: [
          'Zoom avant / arrière / ajuster à l\'écran — échelle du canvas.',
          'Annuler / Rétablir — historique des modifications.',
          'Réinitialiser — restaurer l\'image d\'origine.',
          'Le zoom actuel (%) et les dimensions du frame en pixels sont affichés.',
        ],
      },
      {
        title: 'Calques et propriétés',
        items: [
          'Panneau Calques (gauche) — liste du frame, des images, du texte et des formes ; sélection, réorganisation, verrouillage, suppression.',
          'Panneau Propriétés (droite) — paramètres du calque sélectionné.',
          'Cliquez sur un calque dans le canvas ou dans la liste pour le modifier.',
          'Ajouter une image — calques image supplémentaires depuis votre workspace.',
          'Ctrl+C — copier le calque sélectionné (image, sticker, texte ou forme). Le frame ne peut pas être copié.',
          'Ctrl+V — coller le calque copié avec un léger décalage.',
        ],
        where: 'Boutons de la barre d\'outils avec icônes calques et curseurs.',
      },
      {
        title: 'Frame (mise en page)',
        items: [
          'Taille, arrière-plan, rayon des coins et bordure.',
          'Définir le ratio — choisir un preset (1:1, 16:9, 4:3, etc.) pour le frame.',
          'Lorsque le ratio du frame change, le texte et les formes restent en place et conservent leur taille ; seule la zone visible du frame change.',
          'Le calque image conserve sa position visuelle ; vous pouvez ensuite le redimensionner ou le déplacer comme d\'habitude.',
        ],
        where: 'Panneau Propriétés (droite) lorsque le calque frame est sélectionné.',
        tip: 'Appuyez sur Escape pour quitter le mode ratio.',
      },
      {
        title: 'Calque image',
        items: [
          'Sélectionnez le calque image — le panneau Propriétés affiche « Image ».',
          'Position et taille — champs numériques ; rotation — curseur (−180°…+180°) dans la section Rotation.',
          'Clic droit sur l\'image → mode redimensionnement ; glissez les poignées des coins. Glissez à l\'intérieur de l\'image pour la déplacer dans le frame.',
          'Section Transformation — Modifier avec l\'IA (barre de prompt en bas du canvas, jusqu\'à 200 caractères), Supprimer l\'arrière-plan (utilise le pool partagé de jetons IA), redressement (curseur d\'angle en bas ; cliquez sur Terminé), retournement horizontal / vertical.',
          'Section Transformation — Recadrer (rectangle libre + Découper), Lasso (effacer à l\'intérieur ou conserver la sélection uniquement + coche pour appliquer).',
          'Sections Couleur et Effets — luminosité, contraste, saturation et presets de filtres.',
        ],
        where: 'Panneau Propriétés (droite) lorsqu\'un calque image est sélectionné.',
        tip: 'Modifier avec l\'IA envoie le calque tel qu\'affiché (rotation, retournement, filtres) et le remplace sur le canvas. Escape quitte le redimensionnement, le redressement, le recadrage, le lasso ou la saisie IA. Double appui ferme le tracé lasso sur écran tactile.',
      },
      {
        title: 'Stickers',
        items: [
          'Le bouton « Ajouter un sticker » dans la barre d\'outils ouvre la bibliothèque de stickers de votre workspace.',
          'Un nouveau sticker est créé lorsque vous utilisez Supprimer l\'arrière-plan sur un calque image — le résultat est enregistré dans la bibliothèque et appliqué au calque actif.',
          'Cliquez sur un sticker dans le panneau ou glissez-le sur le canvas pour l\'ajouter comme calque distinct.',
          'Les stickers se comportent comme des images : position, taille, rotation, ombre ; ils apparaissent avec une icône dédiée dans le panneau Calques.',
          'Le × sur une carte de la bibliothèque supprime définitivement le sticker du workspace (les calques déjà sur le canvas ne sont pas affectés).',
        ],
        where: 'Barre d\'outils → icône sticker ; panneau stickers à gauche (à côté du panneau Calques).',
        tip: 'Supprimer l\'arrière-plan utilise le calque tel qu\'affiché, y compris rotation, retournement, échelle et filtres.',
      },
      {
        title: 'Calques texte',
        items: [
          'Ajoutez un calque titre ou corps de texte depuis la barre d\'outils.',
          'Police, taille, couleur, contour, ombre, arrière-plan, espacement.',
          'Alignement — horizontal (gauche / centre / droite) et vertical (haut / centre / bas) dans la zone de texte.',
          'Glissez le texte sur le canvas ; redimensionnez la zone avec les poignées des coins.',
          '« Appliquer à l\'image » — intégrer le texte dans le bitmap avant l\'enregistrement.',
        ],
        where: 'Sur mobile, ouvrez le panneau Propriétés avec l\'icône curseurs.',
      },
      {
        title: 'Formes',
        items: [
          'Rectangle, triangle, cercle, ligne — choisissez un outil et glissez sur le canvas pour dessiner.',
          'Remplissage, contour, rayon des coins, ombre et dégradé dans les propriétés.',
          'Glissez pour déplacer ; poignées des coins pour redimensionner ; poignée supérieure pour pivoter.',
          'Escape annule l\'outil de dessin actif.',
        ],
      },
      {
        title: 'Navigation dans le canvas',
        items: [
          'Souris : glisser pour déplacer ; molette pour zoomer.',
          'Tactile : pincer pour zoomer ; un doigt pour déplacer.',
        ],
      },
      {
        title: 'Enregistrement',
        items: [
          'Enregistrer — écraser le fichier d\'origine.',
          'Télécharger — enregistrer le fichier sur votre ordinateur.',
          'Enregistrer sous — nouveau fichier (nom, PNG/JPG/WebP, taille, dossier).',
        ],
        where: 'Bouton Enregistrer et menu flèche à droite de l\'en-tête de l\'éditeur.',
      },
    ],
  },
  publicGalleries: {
    title: 'Galeries publiques',
    intro: 'Pages soignées pour présenter votre travail aux clients — sans compte requis.',
    blocks: [
      {
        title: 'Galerie à partir d\'un dossier',
        items: [
          'Sélectionnez exactement un dossier.',
          'Menu contextuel → « Créer une galerie » (ou Ctrl+Alt+G).',
          'Définissez le nom, la mise en page, le thème, les informations de l\'auteur.',
          'Copiez le lien et envoyez-le à votre client.',
        ],
      },
      {
        title: 'Galerie par tags',
        items: [
          'Activez le filtre par un ou plusieurs tags.',
          'Cliquez sur « Créer une galerie par tags ».',
          'Le contenu se met à jour automatiquement lorsque vous modifiez les tags des fichiers.',
        ],
        where: 'Le bouton apparaît dans la barre d\'outils lorsque le filtre par tags est actif.',
      },
      {
        title: 'Paramètres de la galerie',
        items: [
          'Mise en page : grille, masonry ou grandes cartes.',
          'Thème : clair/sombre et couleur d\'accent.',
          'Générer une carte de visite avec code QR.',
        ],
      },
      {
        title: 'Panneau auteur',
        items: [
          'Mode « Auteur » — nom, activité, contacts et description.',
          'Mode « Entreprise » — nom, logo depuis les fichiers du workspace, slogan, contacts et « À propos ».',
          'Changer de mode ne supprime pas les données de l\'autre mode.',
          'Les champs remplis apparaissent dans le panneau latéral de la page publique et sur la carte de visite numérique.',
        ],
        where: 'Section « Auteur » dans la fenêtre de création ou d\'édition de la galerie.',
      },
      {
        title: 'Gérer les galeries',
        items: [
          'Barre latérale → « Galeries » — catalogue de toutes les galeries publiques par espace.',
          'Ouvrir, modifier, supprimer ou afficher la carte QR — actions sur la carte.',
        ],
        where: 'Barre latérale → « Galeries » (page /galleries).',
      },
      {
        title: 'Ce que voient les visiteurs',
        items: [
          'Grille d\'images avec défilement infini.',
          'Vue plein écran (Escape, ← →).',
          'Panneau auteur avec les informations de la personne ou de l\'entreprise et les contacts.',
          'Téléchargement de la carte de visite numérique avec code QR vers la galerie.',
        ],
      },
    ],
  },
  profile: {
    title: 'Profil et équipes',
    intro: 'Paramètres personnels, workspaces et membres d\'équipe.',
    blocks: [
      {
        title: 'Ouvrir le profil',
        items: [
          'Élément « Profil » en bas de la barre latérale.',
          '« Se déconnecter » — terminer la session (menu initiales, en haut à droite).',
        ],
        tip: 'Le profil s\'ouvre en page complète ; l\'aide (point d\'interrogation) est un panneau séparé.',
      },
      {
        title: 'Onglet Personnel',
        items: [
          'Modifier le nom et l\'e-mail.',
          'Cliquez sur « Enregistrer les modifications ».',
        ],
      },
      {
        title: 'Onglet Jetons IA',
        items: [
          'Affiche les jetons utilisés et restants du pool partagé (suppression d\'arrière-plan, génération d\'images, édition IA).',
          'Lorsque les jetons sont épuisés, les actions IA sont désactivées jusqu\'à ce qu\'un admin augmente votre limite.',
        ],
        where: 'Profil → Jetons IA.',
      },
      {
        title: 'Onglet Sécurité',
        items: [
          'Modifier le mot de passe : nouveau mot de passe et confirmation.',
          'Le mot de passe doit respecter les exigences de complexité.',
        ],
      },
      {
        title: 'Onglet Workspaces',
        items: [
          'Créer un nouveau workspace (nom, langue, thème, accent).',
          'Modifier ou supprimer votre workspace.',
          'Quitter le workspace d\'un autre utilisateur.',
          'Consulter l\'utilisation du stockage.',
        ],
      },
      {
        title: 'Onglet Équipes',
        items: [
          'Liste des membres pour chacun de vos workspaces, avec places utilisées / max (limite incluant le propriétaire).',
          'Modifier le rôle : Admin, Éditeur, Lecteur (⋮ → Modifier le rôle).',
          'Définir la visibilité : masquer dossiers, tableaux ou boards pour un membre (⋮ → Définir la visibilité).',
          'Retirer un membre du workspace.',
        ],
      },
      {
        title: 'Visibilité',
        items: [
          'La visibilité est une liste d\'interdiction en plus du rôle : une ressource masquée reste inaccessible même si le rôle l\'autoriserait.',
          'Le propriétaire peut masquer des ressources pour tout membre. L\'admin uniquement pour Éditeur et Lecteur — pas pour le propriétaire ni un autre admin.',
          'Masquer un dossier masque aussi ses sous-dossiers et fichiers dans l\'app (listes, recherche, ouverture par URL).',
          'Les tableaux et boards se masquent séparément des dossiers.',
          'Lors d\'une invitation : après le rôle, vous pouvez définir des masquages optionnels avant l\'envoi.',
          'Les liens publics de fichiers fonctionnent toujours, même si le fichier est dans un dossier masqué.',
          'Le dossier racine du workspace ne peut pas être masqué.',
        ],
        where: 'Profil → Équipes → ⋮ sur un membre → Définir la visibilité. Ou cloche → Envoyées → assistant d\'invitation.',
        tip: 'Les masquages s\'appliquent uniquement dans ce workspace et pas dans les autres.',
      },
    ],
  },
  datasets: {
    title: 'Tableaux',
    intro: 'Tableaux de type CRM dans vos espaces — colonnes, lignes et édition inline.',
    blocks: [
      {
        title: 'Où trouver les tableaux',
        items: [
          'Barre latérale → « Données » → « Tous » — catalogue de tous les tableaux par espace (page /data).',
          'Barre latérale → « Données » : liste rapide de vos tableaux et de ceux partagés.',
          '« Créer » sous Données — nouveau tableau dans l\'espace actuel.',
          'Sur la page catalogue — ouvrir, modifier, supprimer, exporter ou voir le schéma via les actions de la carte.',
        ],
        where: 'Barre latérale gauche → Données (et « Tous » pour le catalogue complet).',
      },
      {
        title: 'Ouvrir un tableau',
        items: [
          'Cliquez sur un nom dans la barre latérale ou une carte sur /data — grille CRM sur /data/:id.',
          'Le défilement charge plus de lignes (100 par lot).',
          'La position de défilement est mémorisée au retour sur le même tableau.',
        ],
      },
      {
        title: 'Colonnes et schéma',
        items: [
          '+ dans l\'en-tête — ajouter une colonne (nom, type, options).',
          'Glisser la poignée — réordonner les colonnes.',
          'Glisser le bord droit — redimensionner ; largeurs sauvegardées localement.',
          'Cliquer sur le nom de colonne — renommer (si modification du schéma autorisée).',
          'Menu ⋮ — filtre texte, tri croissant ou décroissant, épingler la colonne au défilement horizontal, réinitialiser tri/filtre ou supprimer la colonne.',
          'Catalogue /data → modifier sur une carte — nom, description et colonnes.',
        ],
      },
      {
        title: 'Tri et filtre',
        text: 'Chaque tableau mémorise un tri et un filtre texte pour votre vue. Les réglages sont enregistrés dans le navigateur et s\'appliquent au chargement des lignes — défilement infini et téléchargement inclus.',
        items: [
          'Ouvrir ⋮ dans l\'en-tête de colonne (toujours visible sur tablette et mobile).',
          'Tri croissant ou décroissant — une petite flèche sur le titre indique le sens actif.',
          'Une seule colonne peut être triée à la fois ; « Réinitialiser le tri » dans le même menu.',
          'Filtre — saisir du texte et cliquer « Appliquer » (ou Entrée) pour garder les lignes où la colonne contient ce texte.',
          'Filtre actif : une petite icône filtre apparaît à côté du titre de colonne (comme pour le tri).',
          '« Effacer le filtre » pour réinitialiser ; changer tri ou filtre recharge les lignes depuis le serveur.',
        ],
        where: 'En-tête de colonne → menu ⋮.',
        tip: 'Tri et filtre sont stockés par tableau sur cet appareil et utilisés lors de l\'export depuis le catalogue des tableaux.',
      },
      {
        title: 'Listes de référence',
        text: 'Une liste de référence, c\'est un petit « tableau-dictionnaire » dans l\'espace — statuts, catégories, SKU et autres valeurs que vous voulez centraliser plutôt que les recopier partout.',
        items: [
          'Création : barre latérale → « Données » → « Créer » → activez « Créer une liste de référence ». Remplissez à la main ou importez tout de suite depuis CSV/Excel.',
          'À l\'intérieur, une seule colonne de valeurs uniques. Comme un tableau classique : ajoutez, modifiez et supprimez des lignes dans la grille.',
          'Catalogue /data → crayon sur la liste : nom, description, renommer la colonne clé, section « Valeurs de la liste ».',
          'Bouton de téléchargement au même endroit — l\'export tient compte du filtre et du tri définis dans la grille.',
          'Import dans une liste existante : ouvrez-la → « Importer CSV/Excel » → choisissez la colonne du fichier avec les clés ; les doublons dans le fichier sont ignorés.',
          'Dans la barre latérale et sur la page catalogue, les listes portent le badge « List ».',
        ],
        where: 'Création : barre latérale → Données → Créer. Gestion : barre latérale → Données → Tous (/data).',
        tip: 'Une liste par espace = une source unique pour statuts, saisons, SKU, etc.',
      },
      {
        title: 'Colonne « Référence » dans un tableau',
        text: 'Quand une cellule doit choisir dans une liste partagée — et non saisir du texte libre — ajoutez une colonne de type Référence et liez-la à la liste voulue.',
        items: [
          'Liste (select) — les options restent dans cette colonne seulement. Pratique pour quelques choix locaux.',
          'Référence — les valeurs viennent d\'une liste de l\'espace. Pratique quand le même ensemble sert dans plusieurs tableaux.',
          'Ajouter la colonne : « + » dans l\'en-tête ou catalogue /data → modifier le tableau → Ajouter une colonne → type Référence → choisir la liste.',
          'Dans la cellule, menu déroulant avec recherche. Après enregistrement, la valeur reste liée à l\'entrée de la liste.',
          'Si vous renommez une valeur dans la liste, le tableau affiche le libellé à jour.',
        ],
        where: 'En-tête du tableau → « + », ou Données → Tous → modifier sur une carte.',
        tip: 'Créez et remplissez d\'abord la liste, puis ajoutez les colonnes Référence dans vos tableaux de travail.',
      },
      {
        title: 'Listes et fichiers',
        text: 'Vous pouvez relier les lignes du tableau aux fichiers de la galerie et mettre à jour automatiquement les tags quand une colonne Référence change.',
        items: [
          'Étape 1 — Liaison avec les fichiers : ouvrez le tableau → « Liaison avec les fichiers » → choisissez la colonne de correspondance (souvent SKU ou code) et le mode d\'association aux tags.',
          'Ensuite la grille affiche une colonne avec l\'aperçu des fichiers liés ; depuis la visionneuse, vous voyez les lignes associées.',
          'Étape 2 — Action fichier sur la colonne Référence : à la création ou modification, réglez « Action fichier » sur Ajouter un tag ou Remplacer le tag dans la liste.',
          'Cela s\'applique quand la ligne a déjà des fichiers liés et que vous modifiez la cellule — les tags se mettent à jour (nom du tag = valeur de la liste).',
          'Sans liaison avec les fichiers configurée avant, les actions de tags ne partent pas — configurez d\'abord le lien tableau ↔ galerie.',
        ],
        where: 'Tableau → Liaison avec les fichiers ; action tags — dans les paramètres de la colonne Référence.',
        tip: 'Exemple : le SKU relie la ligne aux photos produit ; changer le statut dans la liste tague automatiquement « En vente » ou « Archive ».',
      },
      {
        title: 'Fusion de tableaux',
        text: 'Combinez deux tableaux existants en une vue fusionnée en lecture seule. Les lignes sont rapprochées par des colonnes de liaison — pratique pour enrichir des données depuis un autre tableau.',
        items: [
          'Barre latérale → « Données » → « Fusionner » — assistant en trois étapes.',
          'Étape 1 : nom et espace pour le tableau fusionné.',
          'Étape 2 : tableau principal et secondaire, puis colonnes de liaison (dans le second, la colonne de liaison doit être unique).',
          'Étape 3 : choisir les colonnes de chaque tableau à afficher dans le résultat.',
          'Les tableaux fusionnés s\'ouvrent comme une grille habituelle, mais lignes et schéma ne sont pas modifiables — seulement l\'ordre des colonnes, les filtres et le tri.',
          'Dans la barre latérale et sur le catalogue /data, les tableaux fusionnés portent un badge « Merge ».',
        ],
        where: 'Barre latérale gauche → Données → Fusionner (à côté de Créer).',
        tip: 'Préparez d\'abord les deux tableaux sources avec une colonne-clé commune (p. ex. SKU ou ID), marquée comme unique dans le second tableau.',
      },
      {
        title: 'Lignes et cellules',
        items: [
          'Cliquer une cellule pour éditer ; Entrée enregistre, Échap annule.',
          'Tab / Maj+Tab — cellule suivante / précédente.',
          '« Ajouter une ligne » en bas — nouvelle ligne (brouillon vide annulé en cliquant dehors).',
          'Corbeille sur une ligne — supprimer une ligne.',
          'Cases + ⋮ dans l\'en-tête — suppression en masse.',
        ],
        tip: 'Les changements apparaissent immédiatement ; la sync serveur se fait en arrière-plan.',
      },
      {
        title: 'Permissions',
        items: [
          'Propriétaire / Admin — schéma, lignes et métadonnées.',
          'Éditeur — lignes uniquement.',
          'Lecteur — lecture seule.',
        ],
      },
    ],
  },
  boards: {
    title: 'Tableaux Kanban',
    intro: 'Tableaux Kanban sur vos tables — colonnes issues d\'une liste de référence (statuts), cartes issues des lignes.',
    blocks: [
      {
        title: 'Où trouver les tableaux',
        items: [
          'Barre latérale → « Tableaux » → « Tous » — catalogue par espace (page /boards).',
          'Barre latérale → « Tableaux » : liste rapide de vos tableaux et de ceux partagés.',
          '« Créer » sous « Tableaux » — nouveau tableau dans l\'espace courant.',
          'Dans le catalogue — ouvrir, modifier les réglages ou supprimer via le menu de la carte.',
        ],
        where: 'Barre latérale gauche → Tableaux (et « Tous » pour le catalogue complet).',
      },
      {
        title: 'Créer un tableau',
        items: [
          'Étape 1 : nom, description optionnelle et espace de travail.',
          'Étape 2 : table éligible et colonne de référence pour les statuts ; cochez les valeurs qui deviennent des colonnes (au moins une).',
          'Étape 3 : champ titre et champs optionnels affichés sur les cartes.',
          'Après création, la page /boards/:id s\'ouvre.',
        ],
        tip: 'Seules les tables ordinaires avec une colonne de référence. Les datasets merge et list ne sont pas proposés.',
      },
      {
        title: 'Tableau Kanban',
        items: [
          'Les colonnes suivent l\'ordre des statuts ; faites glisser la poignée pour réordonner (admin).',
          'Les cartes montrent le titre et quelques champs d\'aperçu.',
          'Une icône message avec un compteur apparaît sur une carte lorsqu\'il y a des commentaires.',
          'Une icône trombone avec un compteur apparaît lorsqu\'il y a des fichiers joints.',
          'Déplacez les cartes entre colonnes ou dans une colonne — statut et ordre changent.',
          'Mises à jour en direct via SSE — les autres voient déplacements, éditions, commentaires et attache/détache de fichiers sans recharger.',
        ],
      },
      {
        title: 'Cartes',
        items: [
          'Clic sur une carte — modale de lecture avec les champs, l\'onglet Commentaires et l\'onglet Fichiers.',
          '⋮ → Modifier — panneau latéral de la ligne.',
          '⋮ → Supprimer — retire la ligne après confirmation.',
          '« Ajouter une carte » sous une colonne — nouvelle ligne avec ce statut.',
        ],
        where: 'Page du tableau → carte ou pied de colonne.',
        tip: 'Le lien /boards/:id?card=:rowId ouvre la même modale (ex. depuis la cloche).',
      },
      {
        title: 'Commentaires de carte',
        items: [
          'Les commentaires appartiennent à ce tableau + cette carte (ligne), pas à toutes les boards de la même ligne.',
          'Tout membre du workspace (lecteur+) peut commenter ; l\'auteur ou un admin peut supprimer les commentaires des autres.',
          'Les nouveaux commentaires notifient les membres dans la cloche.',
        ],
      },
      {
        title: 'Fichiers sur les cartes',
        items: [
          'Onglet Fichiers — joindre des images depuis l\'espace du tableau (même sélecteur que dans l\'éditeur d\'images).',
          'Les pièces jointes appartiennent à ce tableau + cette carte (ligne), pas à toutes les boards de la même ligne.',
          'Cliquez sur un aperçu pour ouvrir la visionneuse ; le survol montre le nom ; ✕ détache sans supprimer le fichier de la galerie.',
          'Si le fichier est dans la Corbeille, la pièce jointe reste avec un overlay « dans la corbeille » ; la suppression définitive retire la pièce jointe.',
          'Tout membre du workspace (lecteur+) peut joindre et détacher.',
        ],
        where: 'Modale de carte → onglet Fichiers.',
      },
      {
        title: 'Colonnes',
        items: [
          'Admin : ajouter une colonne (valeur de statut nouvelle ou existante), renommer, réordonner ou retirer.',
          'Une colonne avec des cartes ne peut pas être retirée — déplacez ou supprimez d\'abord les cartes.',
          'Retirer une colonne la masque seulement sur ce tableau ; la valeur reste dans la liste de référence.',
        ],
      },
      {
        title: 'Responsables et échéances',
        items: [
          'Ouvrez une carte → Responsables : ajoutez des membres du workspace, chacun avec sa propre échéance et colonne cible.',
          'Seuls Propriétaire / Admin peuvent assigner, modifier échéances/colonnes ou définir la priorité (du plus bas au plus haut).',
          'Éditeur voit tous les responsables ; Lecteur ne voit que sa propre assignation sur la carte.',
          'Glisser la carte dans la colonne cible d\'un responsable termine sa tâche ; la ramener la rouvre.',
          'Plusieurs personnes avec la même colonne cible se terminent en un seul déplacement.',
          'Les assignations en retard sont signalées sur la carte ; les alertes arrivent dans la cloche au login / refresh du jeton (pas de cron).',
          'Alertes personnelles pour vos échéances (bientôt / aujourd\'hui / en retard). Propriétaire/Admin reçoivent aussi un digest si quelqu\'un d\'autre est en retard.',
        ],
        where: 'Tableau → modale de carte → Responsables (et Priorité sous les champs).',
        tip: 'Changer l\'échéance réinitialise l\'état des alertes — le prochain login/refresh pourra notifier à nouveau.',
      },
      {
        title: 'Permissions',
        items: [
          'Lecteur — voir le tableau, déplacer les cartes, commenter et joindre/détacher des fichiers ; voit seulement ses propres responsables.',
          'Éditeur — créer, modifier et supprimer des cartes (lignes) ; voit tous les responsables mais ne peut pas assigner.',
          'Propriétaire / Admin — réglages, colonnes, suppression, modération des commentaires, assignations, échéances/colonnes et priorité.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Workflows',
    intro: 'Les workflows sont une couche produit à côté des Fichiers, Données, Tableaux et Galeries — pas un écran de réglages. Vous construisez un graphe d\'automation qui fait circuler fichiers, lignes et cartes dans des systèmes déjà en place.',
    blocks: [
      {
        title: 'Pas des réglages',
        items: [
          'Les workflows ont leur propre section dans la barre latérale — comme Données ou Tableaux, pas sous Profil / réglages.',
          'Chaque processus appartient à un espace et utilise ses dossiers, tables, tableaux, motifs et tags.',
          'Activer démarre le processus ; Enregistrer écrit le graphe. Les exécutions montrent ce qui s\'est passé.',
        ],
        tip: 'Langue, thème et compte — c\'est le Profil, pas les Workflows.',
      },
      {
        title: 'Où trouver les workflows',
        items: [
          'Barre latérale → « Workflows » → « Tous » — catalogue par espace (/workflows).',
          'Barre latérale → « Workflows » → « Actuels » — liste rapide de l\'espace actif (masquée si vide).',
          '« Créer » sous Workflows — nouveau graphe (owner/admin uniquement).',
          'Ouvrir un processus — constructeur de graphe ; Exécutions — journal.',
        ],
        where: 'Barre latérale gauche → Workflows.',
      },
      {
        title: 'Constructeur de graphe',
        items: [
          'Palette — groupes : Déclencheurs, Fichiers, Contenu, AI, Table, Board, Flow ; plus le panneau Modèles.',
          'Canevas — placer les nœuds, relier, déplacer ; Annuler / Rétablir à côté du titre (aussi Ctrl+Z / Ctrl+Y).',
          'Inspecteur — configurer le déclencheur ou nœud sélectionné (dossier, tags, table, cron, etc.).',
          'Exactement un déclencheur. Souvent une chaîne ; If / branche peut sortir en True et False.',
          '« Vérifier » contrôle les champs obligatoires et le flux (fichiers / lignes) avant Enregistrer.',
          'Clavier : copier / coller des nœuds (Ctrl+C / Ctrl+V), supprimer la sélection (Suppr / Retour) — voir Raccourcis.',
        ],
        tip: 'Cron : une fois, chaque jour, chaque semaine (jour + heure) ou chaque mois (date + heure), avec fuseau.',
      },
      {
        title: 'Palette : Déclencheurs',
        text: 'Exactement un déclencheur par processus.',
        items: [
          'Fichier importé — démarre à l\'upload dans le dossier suivi.',
          'Ligne ajoutée — démarre à l\'ajout d\'une ligne de table.',
          'Lignes importées — démarre après un import CSV/Excel.',
          'Cell matched — démarre quand une cellule prend la valeur attendue.',
          'File moved — démarre quand un fichier est déplacé vers le dossier suivi.',
          'On schedule — démarre selon un cron (le run commence vide).',
        ],
      },
      {
        title: 'Palette : Fichiers',
        items: [
          'Appliquer des tags — ajoute des tags aux fichiers du flux.',
          'Retirer des tags — retire des tags des fichiers du flux.',
          'Déplacer le fichier — déplace les fichiers vers le dossier cible.',
          'Mettre à la corbeille — envoie les fichiers à la corbeille.',
          'Linked rows — trouve les lignes de table liées aux fichiers du flux.',
        ],
      },
      {
        title: 'Palette : Contenu',
        items: [
          'Apply pattern — applique un motif d\'espace à la scène de chaque fichier.',
          'Save as — enregistre une copie de chaque fichier dans le dossier cible.',
          'Replace content — remplace chaque fichier par le détourage de Remove background.',
        ],
      },
      {
        title: 'Palette : AI',
        items: [
          'Remove background — enlève le fond de chaque image (jetons AI).',
          'Generate image — une nouvelle image par fichier du flux.',
        ],
        tip: 'Les nœuds AI utilisent le pool de jetons partagé du profil.',
      },
      {
        title: 'Palette : Table',
        items: [
          'Définir des cellules — écrit des valeurs dans les colonnes choisies des lignes du flux.',
          'Insert row — ajoute une nouvelle ligne à la table.',
          'Linked files — charge dans le flux les fichiers liés aux lignes.',
        ],
      },
      {
        title: 'Palette : Board',
        items: [
          'Add priority — définit la priorité des cartes du flux.',
          'Clear priority — retire la priorité des cartes du flux.',
          'Ajouter une assignation — assigne un membre (échéance/colonne) aux cartes.',
          'Unassign — retire les assignés des cartes du flux.',
        ],
      },
      {
        title: 'Palette : Flow',
        items: [
          'Table source — alimente Generate image en lignes de table (entrée data).',
          'Search — charge des fichiers de l\'espace dans le flux par tags, nom ou extension.',
          'Filter — ne garde que les éléments du flux qui correspondent.',
          'Link — associe les fichiers aux valeurs d\'un dictionnaire par nom (tag optionnel).',
          'Limit — ne garde que les N premiers éléments du flux.',
          'If / branch — oriente le run vers True ou False sans changer le stream.',
        ],
      },
      {
        title: 'Qui peut quoi (lecture seule pour l\'éditeur)',
        items: [
          'Owner / Admin — créer, éditer, enregistrer, activer/désactiver, supprimer et gérer les modèles.',
          'Éditeur / Lecteur — catalogue et graphe en lecture seule (sans palette ni Save), plus les Exécutions.',
        ],
        tip: 'Les éditeurs peuvent étudier le graphe et les runs — sans modifier l\'automation.',
      },
      {
        title: 'Exécutions / journal',
        items: [
          'Chaque run enregistre le déclencheur et chaque étape : ok, skipped ou failed, avec durée et détails.',
          'Ouvrir un run — graphe avec badges d\'étapes et liste des étapes.',
          'Les exécutions sont visibles par tout membre de l\'espace.',
        ],
        where: 'Processus → Exécutions, ou carte du catalogue → Runs.',
      },
      {
        title: 'Modèles utilisateur',
        items: [
          'Owner / Admin : menu Enregistrer → « Enregistrer comme modèle » — graphe dans la bibliothèque de l\'espace.',
          'Panneau Modèles (gauche) — appliquer un modèle sur le canevas (copie avec de nouveaux ids) ou supprimer.',
          'Appliquer ne crée pas de lien live — vous éditez la copie comme n\'importe quel processus.',
        ],
        tip: 'Les modèles sont par espace. Les éditeurs ne peuvent ni créer ni appliquer.',
      },
    ],
  },
  notifications: {
    title: 'Notifications et invitations',
    intro: 'La cloche dans la barre supérieure est votre boîte de réception pour les événements d\'équipe et de commentaires.',
    blocks: [
      {
        title: 'Onglet Commentaires',
        items: [
          'Alertes pour les nouveaux commentaires et réponses sur vos fichiers.',
          'Alertes pour les commentaires sur les cartes de tableau.',
          'Échéances : bientôt (24 h), aujourd\'hui, en retard — si vous êtes responsable.',
          'Digest Propriétaire/Admin : quelqu\'un d\'autre sur vos tableaux est en retard (environ une fois par jour par assignation).',
          'Cliquez sur une alerte fichier — ouvre l\'image et l\'onglet Commentaires.',
          'Cliquez sur une alerte carte ou échéance — ouvre /boards/:id?card=:rowId avec la modale.',
          '« Tout effacer » — supprime toutes les notifications du workspace actif.',
        ],
        where: 'Cloche → onglet icône message (ouvert par défaut).',
      },
      {
        title: 'Onglet Reçues',
        items: [
          'Invitations à rejoindre le workspace d\'un autre utilisateur.',
          'Accepter ou refuser.',
          'Affiche : qui a invité, nom du workspace, rôle, expiration.',
        ],
      },
      {
        title: 'Onglet Envoyées',
        items: [
          'Invitations que vous avez envoyées.',
          'Annuler une invitation en attente.',
          '« Inviter » — inviter un nouvel utilisateur.',
        ],
      },
      {
        title: 'Comment inviter un utilisateur',
        items: [
          'Cloche → Envoyées → « Inviter ».',
          'Étape 1 : rechercher l\'utilisateur par e-mail.',
          'Étape 2 : choisir le workspace et le rôle (Admin / Éditeur / Lecteur). Les invitations en attente comptent aussi.',
          'Quand l\'équipe de l\'espace est pleine, l\'invitation est désactivée jusqu\'à ce qu\'un admin système augmente votre limite.',
          'Étape 3 : masquer éventuellement dossiers, tableaux ou boards, puis envoyer l\'invitation.',
        ],
      },
      {
        title: 'Badge sur la cloche',
        text: 'Le badge rouge indique le total des invitations en attente et des alertes de commentaires pour le workspace actif.',
      },
    ],
  },
  trash: {
    title: 'Corbeille',
    intro: 'Les fichiers et dossiers supprimés arrivent ici au lieu de disparaître immédiatement.',
    blocks: [
      {
        title: 'Comment ouvrir',
        items: [
          'Cliquez sur « Corbeille » dans la barre d\'outils de l\'écran d\'accueil.',
          'Bouton Retour dans l\'en-tête de la corbeille — revenir à la galerie.',
        ],
        where: 'Icône corbeille à droite de la barre d\'outils.',
      },
      {
        title: 'Restaurer',
        items: [
          'Sélectionnez des éléments → menu contextuel → « Restaurer ».',
          'Raccourci : Ctrl+Alt+R.',
          'Les éléments reviennent à leur emplacement d\'origine.',
        ],
      },
      {
        title: 'Supprimer définitivement',
        items: [
          '× sur une carte — supprimer un fichier ou un dossier de façon permanente (propriétaire uniquement).',
          '« Vider la corbeille » — tout supprimer (avec confirmation, propriétaire uniquement).',
        ],
        tip: 'Après avoir vidé la corbeille, les fichiers ne peuvent plus être récupérés.',
      },
      {
        title: 'Limitations de la corbeille',
        items: [
          'Impossible d\'importer des fichiers ou de créer des dossiers.',
          'Impossible de glisser-déposer des éléments.',
          'Le tri est indisponible.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Raccourcis et gestes',
    intro: 'Aide-mémoire rapide. Appuyez sur Shift+? pour ouvrir cette fiche à tout moment. Les raccourcis sont désactivés lorsque l\'éditeur est ouvert ou que le focus est dans un champ de saisie.',
    blocks: [
      {
        title: 'Galerie',
        items: [
          'Ctrl+A — tout sélectionner.',
          'Ctrl+U — importer des fichiers.',
          'Ctrl+Shift+V — coller une capture d\'écran depuis le presse-papiers.',
          'Ctrl+Alt+N — créer un dossier.',
          'Ctrl+X — couper.',
          'Ctrl+V — coller.',
          'F2 — renommer le dossier (un sélectionné).',
          'Ctrl+Alt+G — créer une galerie (un dossier).',
          'Ctrl+Alt+T — ajouter un tag.',
          'Delete — envoyer vers la corbeille.',
          'Ctrl+Alt+R — restaurer (dans la corbeille).',
          'Escape — effacer la sélection.',
        ],
      },
      {
        title: 'Visionneuse de fichiers',
        items: [
          '← / → — fichier précédent / suivant.',
          'Escape — fermer la fenêtre.',
        ],
      },
      {
        title: 'Éditeur',
        items: [
          'Ctrl+Z — annuler.',
          'Ctrl+Y — rétablir.',
          'Ctrl+0 — ajuster à l\'écran.',
          'Ctrl++ — zoom avant.',
          'Ctrl+- — zoom arrière.',
          'H — retournement horizontal (image sélectionnée).',
          'V — retournement vertical (image sélectionnée).',
          'T — mode redressement.',
          'C — recadrage libre.',
          'L — lasso effacer à l\'intérieur.',
          'Shift+L — lasso conserver la sélection uniquement.',
          'Ctrl+C — copier le calque sélectionné (pas le frame).',
          'Ctrl+V — coller le calque.',
          'Delete / Backspace — supprimer le calque sélectionné (texte, image ou forme).',
          'Escape — quitter le mode actif (ratio, redimensionnement, outil forme, recadrage, lasso) ou réinitialiser les modifications.',
        ],
        tip: 'Les raccourcis apparaissent dans les infobulles des boutons de l\'éditeur (sur bureau avec souris). Le ratio du frame et les outils de calque n\'ont pas de raccourcis lettre — utilisez le panneau Propriétés.',
      },
      {
        title: 'Tableaux (grille CRM)',
        items: [
          'Entrée — enregistrer la cellule ou commencer l\'édition.',
          'Échap — annuler l\'édition de cellule.',
          'Tab / Maj+Tab — cellule suivante / précédente.',
        ],
        where: 'Ouvrez un tableau depuis la barre latérale.',
      },
      {
        title: 'Éditeur de processus (workflows)',
        items: [
          'Ctrl+Z — annuler.',
          'Ctrl+Y ou Ctrl+Maj+Z — rétablir.',
          'Ctrl+C — copier les nœuds sélectionnés.',
          'Ctrl+V — coller les nœuds.',
          'Suppr / Retour arrière — supprimer les nœuds sélectionnés.',
        ],
        where: 'Ouvrez un processus depuis la barre latérale (Workflows).',
        tip: 'Les raccourcis utilisent les touches physiques (y compris sur disposition non latine). Désactivés si le focus est dans un champ ou si le processus est en lecture seule.',
      },
      {
        title: 'Gestes tactiles',
        items: [
          'Appui simple — ouvrir ; double appui — sélectionner.',
          'Rubber band — sélection multiple.',
          'Appui long — menu contextuel.',
          'Pincer dans l\'éditeur — zoom.',
        ],
      },
    ],
  },
};
